import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// ============================================================
// THEME PROVIDER
// ------------------------------------------------------------
// This app is Vite + React Router, so there is no `next-themes`. The design
// system's tokens key off a `data-theme` attribute on <html>, and this is what
// sets it.
//
// CRITICAL: the dark palette lives in `:root` and light overrides it under
// `[data-theme="light"]`. So WITHOUT this attribute every token resolves DARK:
// near-white text on a near-white page. index.html hardcodes data-theme="light"
// so the first paint is already correct; this provider owns it from then on.
//
// Light-only for now. The dark tokens ship in chekit-tokens.css and the plumbing
// below is complete, so turning dark mode on is a matter of exposing a toggle,
// not a rewrite. It is deliberately not wired up yet: it would double the visual
// QA surface across 37 pages.
// ============================================================

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'chekit-theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;

  // LIGHT is the default for a new user, NOT the OS preference.
  //
  // The spec is explicit (§2: `defaultTheme="light"`), and the other three
  // portals all behave this way. Following the OS here would mean an operator on
  // a dark-mode laptop sees a different Chekit than one who isn't, before either
  // has chosen anything.
  return 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = (t: Theme) => setThemeState(t);
  const toggleTheme = () => setThemeState((t) => (t === 'light' ? 'dark' : 'light'));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
